import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, theme, Button, message, Input, Modal } from "antd";

import { GoldOutlined, PlusOutlined } from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { GroupTable } from "../../components/Table";
import { AddGroupModal } from "../../components/ModalForm";
import { EditGroupModal } from "../../components/ModalForm";
import GroupStatementModal from "../../components/ModalForm/Group/GroupStatementModal";
import BulkPaymentModal from "../../components/ModalForm/Group/BulkPaymentModal";

import { AuthContext } from "../../context/AuthContext";
import { TourContext } from "../../context/TourContext";

import {
  getGroupsApi,
  getGroupApi,
  getUserGroupsApi,
  addGroupApi,
  editGroupApi,
  removeGroupApi,
  searchGroupsApi,
} from "../../services/Group/GroupApis";

const { Content } = Layout;
const { Search } = Input;

const GroupsPage: React.FC = () => {
  const { currentUser, currentGroup, setCurrentUser } = useContext(AuthContext);
  const { refAddGroup, markPageAsVisited, pages, setOpen, setPageLoaded } =
    useContext(TourContext);

  const [groups, setGroups] = useState<any>([]);
  const [group, setGroup] = useState<any>({});
  const [filtered, setFiltered] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<string>(
    () => sessionStorage.getItem("groupsSearch") || ""
  );
  const [show, setShow] = useState(false);
  const [update, setUpdate] = useState(false);
  const [isActive, setActive] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false); // State for confirmation modal
  const [groupToDelete, setGroupToDelete] = useState<string>(""); // State for confirmation modal
  const [key, setKey] = useState<string>(""); // State for confirmation modal
  const [showStatement, setShowStatement] = useState(false);
  const [selectedGroupForStatement, setSelectedGroupForStatement] = useState<any>(null);
  const [showBulkPayment, setShowBulkPayment] = useState(false);
  const [selectedGroupForPayment, setSelectedGroupForPayment] = useState<any>(null);

  const navigate = useNavigate();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const showModal = () => {
    setShow(!show);
    setUpdate(false);
  };

  const showEditModal = (data: any) => {
    setGroup(data);
    setShow(!show);
    setUpdate(true);
  };

  const handleManage = (id: string) => {
    navigate(`/groups/${id}`);
  };

  const handleShowStatement = (groupData: any) => {
    setSelectedGroupForStatement(groupData);
    setShowStatement(true);
  };

  const handleCloseStatement = () => {
    setShowStatement(false);
    setSelectedGroupForStatement(null);
  };

  const handleShowBulkPayment = (groupData: any) => {
    setSelectedGroupForPayment(groupData);
    setShowBulkPayment(true);
  };

  const handleCloseBulkPayment = () => {
    setShowBulkPayment(false);
    setSelectedGroupForPayment(null);
  };

  const handleAdd = async (data: any) => {
    const groupInfo = await getGroupApi(currentGroup);
    setActive(true);

    const { GroupType, ...rest } = data;
    const payload = {
      ...rest,
      GroupRelationshipID: currentGroup ? groupInfo?.data?.id ?? null : null,
      IsExternal: GroupType === "External",
    };

    return await addGroupApi(
      payload,
      currentUser.id,
      !currentGroup ? undefined : groupInfo.data.id,
      !currentGroup ? 1 : groupInfo.data.GroupLevel
    )
      .then((res) => {
        setGroups([...groups, res.data]);

        // Make the new group selectable in the TopBar dropdown immediately
        // (dropdown reads from currentUser.Group which is decoded from the JWT at login).
        if (res?.data && setCurrentUser) {
          setCurrentUser((prev: any) => {
            if (!prev) return prev;
            const existingGroups = Array.isArray(prev.Group) ? prev.Group : [];
            if (existingGroups.some((g: any) => String(g.id) === String(res.data.id))) {
              return prev;
            }
            return { ...prev, Group: [...existingGroups, res.data] };
          });
        }

        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleUpdate = async (id: string, data: any) => {
    setActive(true);

    return await editGroupApi(id, data)
      .then((res) => {
        const uppdatedGroups = groups.map((group: any) => {
          if (id === group.id) {
            return { ...group, ...res.data };
          }
          return group;
        });

        setGroups(uppdatedGroups);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleDelete = (id: string, key: string) => {
    setKey(key);
    setConfirmDelete(true);
    setGroupToDelete(id);
  };

  const onSearch = async (e: any) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value) sessionStorage.setItem("groupsSearch", value);
    else sessionStorage.removeItem("groupsSearch");
    if (value !== "") {
      const result = await searchGroupsApi(String(currentGroup), value);
      const groupIds = new Set(groups.map((g: any) => g.id));
      setFiltered(result.data.filter((g: any) => groupIds.has(g.id)));
    } else {
      setFiltered([]);
    }
  };

  const onCancelDelete = () => {
    setConfirmDelete(false);
    setGroupToDelete("");
  };

  const onConfirmDelete = () => {
    setConfirmDelete(false);
    setActive(true);

    removeGroupApi(groupToDelete)
      .then((res) => {
        getGroupsApi()
          .then((res) => {
            setActive(false);
            setGroups(res.data);
          })
          .catch((err) => {
            setActive(false);
            console.log(err);
          });
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  useEffect(() => {
    getGroupsApi()
      .then((res) => {
        setGroups(res.data);
        setActive(false);
        setPageLoaded(true);
        setOpen(true);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [currentGroup]);

  useEffect(() => {
    if (pages.AddGroup) {
      markPageAsVisited("AddGroup");
    }
  }, []);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <GoldOutlined
                  style={{ paddingRight: "5px", color: "#f04f05" }}
                />
                Groups
              </h2>
              <div className="interactions" ref={refAddGroup}>
                <Button onClick={showModal} size="large">
                  <PlusOutlined style={{ paddingRight: "5px" }} />
                  Add Group
                </Button>

                <div>
                  <Search
                    placeholder="Search"
                    size="large"
                    value={searchValue}
                    onChange={(e) => onSearch(e)}
                    style={{ width: "100%", margin: "24px 0" }}
                  />
                </div>

                {update ? (
                  <EditGroupModal
                    show={show}
                    group={group}
                    showModal={showModal}
                    handleUpdate={handleUpdate}
                  />
                ) : (
                  <AddGroupModal
                    show={show}
                    showModal={showModal}
                    handleAdd={handleAdd}
                  />
                )}
              </div>

              <GroupTable
                groups={groups}
                filtered={filtered}
                showEditModal={showEditModal}
                handleManage={handleManage}
                handleDelete={handleDelete}
                showStatementModal={handleShowStatement}
                showBulkPaymentModal={handleShowBulkPayment}
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      {/* Group Statement Modal */}
      {selectedGroupForStatement && (
        <GroupStatementModal
          show={showStatement}
          group={selectedGroupForStatement}
          showModal={handleCloseStatement}
        />
      )}

      {/* Bulk Payment Modal */}
      {selectedGroupForPayment && (
        <BulkPaymentModal
          show={showBulkPayment}
          group={selectedGroupForPayment}
          showModal={handleCloseBulkPayment}
          onSuccess={() => {
            // Optionally refresh data after successful payment
            message.success("Bulk payment processed successfully");
          }}
        />
      )}

      {/* Confirmation Modal */}
      {key === "1" ? (
        <Modal
          title="Confirm Remove"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this Group inactive?
        </Modal>
      ) : (
        <Modal
          title="Confirm Revert"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this Group active?
        </Modal>
      )}
    </LoadingOverlay>
  );
};

export default GroupsPage;
